import { Environment } from 'fym-common/src/config/types';
import type {
  Music24EventName,
  Music24EventProps,
} from '../../../../../music24-api/src/productEvents/music24EventCatalog';
import { config } from '../../../config/config';
import { SMLGenericAnalytics } from './genericAnalytics';
import { screenOfPath } from './screenOfPath';

type SmartLinkType = 'custom' | 'playlist' | 'release' | 'presave';
type SmartLinkMedia = 'track' | 'album' | 'playlist' | 'artist' | 'other';

/** The `media` prop of `smartlink_create_started` from a search type. */
export const smartLinkMediaOf = (type: string): SmartLinkMedia =>
  type === 'track' || type === 'album' || type === 'artist' ? type : 'other';

/**
 * The product analytics of the web app. Every name is an event of the Music24 catalog
 * (`music24-api/src/productEvents/music24EventCatalog.ts`), so the web app and both native apps
 * send one funnel. Nothing leaves the browser before the visitor accepts the consent bar.
 */
export const SMLAnalytics = {
  ...SMLGenericAnalytics,
  async track<N extends Music24EventName>(event: N, data?: Music24EventProps<N>) {
    if (config.environment === Environment.local) {
      console.log(`Analytics: ${event}\n${JSON.stringify(data)}`);
    }
    await SMLGenericAnalytics.track(event, data);
  },
  /** Sends the screen of a router path (the route name, never the ids of the path). */
  async screenPath(path: string) {
    const [pathname] = path.split('?');
    if (pathname === undefined || pathname === '') {
      return;
    }
    await SMLGenericAnalytics.screen(screenOfPath(pathname));
  },
  async trackSupportOpened(source: string) {
    await SMLAnalytics.track('support_opened', { source });
  },
  async trackSmartLinkCreateOpened(type: SmartLinkType | 'picker') {
    await SMLAnalytics.track('smartlink_create_opened', { type });
  },
  async trackSmartLinkCreateStarted(type: 'release' | 'playlist', media: SmartLinkMedia) {
    await SMLAnalytics.track('smartlink_create_started', { type, media });
  },
  async trackSmartLinkCreated(type: SmartLinkType) {
    await SMLAnalytics.track('smartlink_created', { type });
  },
  async trackSmartLinkCustomizeStarted() {
    await SMLAnalytics.track('smartlink_customize_started', {});
  },
  async trackSmartLinkCustomizeChanged() {
    await SMLAnalytics.track('smartlink_customize_changed', {});
  },
  async trackSignInMethodChosen(
    method: 'email' | 'apple' | 'google' | 'password',
    mode: 'signin' | 'signup' | 'invite'
  ) {
    await SMLAnalytics.track('signin_method_chosen', { method, mode });
  },
  async trackSignInCompleted(method: 'email' | 'apple' | 'google' | 'password', newUser: boolean) {
    await SMLAnalytics.track('signin_completed', { method, newUser });
  },
};

/** Sends one catalog event from a click or an effect, where nobody waits for the answer. */
export function trackEvent<N extends Music24EventName>(event: N, data?: Music24EventProps<N>): void {
  SMLAnalytics.track(event, data).catch(console.error);
}
